import React, { useCallback, useEffect, useMemo } from "react";

import Comment from "antd/es/comment";
import cn from "classnames";
import { useIntl } from "react-intl";

import Note from "@mapmycustomers/shared/types/entity/common/Note";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { stopEventPropagation } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Avatar } from "@mapmycustomers/ui";

import Lexer from "@app/component/Lexer";
import LongContentParagraph from "@app/component/typography/LongContentParagraph";
import MultilineText from "@app/component/typography/MultilineText";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { formatRawDateRelative, getUserDisplayNameWithInfo } from "@app/util/formatters";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";
import useSizeObserver from "@app/util/hook/useSizeObserver";

import EditNoteForm from "./EditNoteForm";
import styles from "./NoteItem.module.scss";

const EXTRA_PADDING = 16;

export interface NoteItemProps {
  canEdit?: boolean;
  deleteLoading?: boolean;
  index: number;
  note: Note;
  onChange?: (note: Note) => void;
  onDelete?: (note: Note) => void;
  onHeightChange?: (note: Note, index: number, height: number) => void;
  onNotesChange?: (notesChanged: boolean) => void;
  saveLoading?: boolean;
  teams: Team[];
  users: User[];
}

const NoteItem: React.FC<NoteItemProps> = ({
  canEdit = true,
  deleteLoading,
  index,
  note,
  onChange,
  onDelete,
  onHeightChange,
  onNotesChange,
  saveLoading,
  teams,
  users,
}) => {
  const intl = useIntl();
  const [isEditing, startEditing, stopEditing] = useBoolean();

  useModalVisibilityAnalyticEffect("Edit Note", isEditing, useAnalytics());

  useEffect(() => {
    onNotesChange?.(isEditing);
  }, [isEditing, onNotesChange]);

  const [, height, ref] = useSizeObserver<HTMLDivElement>();
  useEffect(() => {
    if (height > 0) {
      onHeightChange?.(note, index, height + EXTRA_PADDING);
    }
  }, [height, note, index, onHeightChange]);

  const handleDelete = useCallback(() => {
    stopEditing();
    onDelete?.(note);
  }, [note, onDelete, stopEditing]);

  const editedByUserName = useMemo(() => {
    const user = users.find(({ id }) => id === note.updatedBy && id !== note.user.id);

    return user ? getUserDisplayNameWithInfo(intl, user) : undefined;
  }, [intl, note.updatedBy, note.user.id, users]);

  return (
    <div
      className={cn(styles.container, { [styles.edit]: canEdit && !isEditing })}
      onClick={canEdit ? startEditing : undefined}
      ref={ref}
    >
      <Comment
        author={getUserDisplayNameWithInfo(intl, note.user)}
        avatar={<Avatar size="small" tooltip={false} user={note.user} />}
        content={
          isEditing ? (
            <div onClick={stopEventPropagation}>
              <EditNoteForm
                isDeleting={deleteLoading}
                isSaving={saveLoading}
                noteText={note.note}
                onCancel={stopEditing}
                onDelete={handleDelete}
                onSave={(noteText: string) => {
                  stopEditing();
                  onChange?.({ ...note, note: noteText });
                }}
              />
            </div>
          ) : (
            <div className={styles.content}>
              <LongContentParagraph noMargin>
                <MultilineText
                  wrapLinesWith={Lexer}
                  wrapperProps={{ stopEventPropagation: true, teams, users }}
                >
                  {note.note}
                </MultilineText>
              </LongContentParagraph>
              {editedByUserName && (
                <div className={styles.updatedBy}>
                  {intl.formatMessage(
                    {
                      id: "recordPane.noteList.noteItem.editedBy",
                      defaultMessage: "Last edited by {name}",
                      description: "Edited by label",
                    },
                    { name: editedByUserName }
                  )}
                </div>
              )}
            </div>
          )
        }
        datetime={formatRawDateRelative(note.updatedAt, Date.now(), "", "PPPp")}
      />
    </div>
  );
};

export default NoteItem;
